import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { NavigationExtras, Router } from '@angular/router';
import { PushService, RegisterDeviceDto } from '@careapp-group/ng-sdk-v2-new';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, mapTo, switchMap, switchMapTo, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ShowSnackBar } from '../store';
import { auth } from '../store/auth/auth.selectors';
import { SnackBarData } from '../store/snackbar/snackbar-data';
import {
  didReceiveNotification,
  failRegisterDevice,
  FirebaseNotificationPayload,
  initializePush,
  initializePushFailed,
  notificationSubscriptionError,
  pushInitSuccess,
  registerDevice,
  successRegisterDevice,
} from './push-notifications.actions';
import { isPushEnabled } from './push-notifications.reducer';

@Injectable({ providedIn: 'root' })
export class PushNotificationEffects {
  initializePushPermissions$ = createEffect(() =>
    this.actions.pipe(
      ofType(initializePush),
      switchMapTo(this.afMessaging.requestPermission),
      mapTo(pushInitSuccess()),
      catchError((error) => of(initializePushFailed({ error })))
    )
  );

  acceptTokens$ = createEffect(() =>
    this.store.pipe(
      select(isPushEnabled),
      filter((enabled) => !!enabled),
      take(1),
      switchMapTo(this.afMessaging.tokenChanges),
      map((deviceToken) => registerDevice({ deviceToken })),
      catchError((error) => of(initializePushFailed({ error })))
    )
  );

  notificationPermissionError$ = createEffect(() =>
    this.actions.pipe(
      ofType(initializePushFailed),
      map((action) => {
        if (action.error?.code === 'messaging/permission-blocked') {
          return ShowSnackBar({
            message: 'Push permissions are blocked. Please check your browser settings.',
          });
        }
      }),
      filter((action) => !!action)
    )
  );

  submitTokensToServer$ = createEffect(() =>
    combineLatest(
      this.actions.pipe(ofType(registerDevice)),
      this.store.pipe(
        select(auth.getLoggedInUser),
        filter((user) => !!user)
      )
    ).pipe(
      switchMap(([{ deviceToken }, user]) => {
        const appVersion = environment.APP_VERSION;
        const appId = environment.APP_ID;
        return this.pushService.registerDevice({
          registerDeviceDto: {
            deviceToken,
            deviceType: 'web',
            appVersion,
            appId,
          } as RegisterDeviceDto,
        });
      }),
      mapTo(successRegisterDevice()),
      catchError((error) => of(failRegisterDevice({ error })))
    )
  );

  receiveNotifications$ = createEffect(() =>
    this.afMessaging.messages.pipe(
      map((payload: FirebaseNotificationPayload) =>
        didReceiveNotification({
          payload,
        })
      ),
      catchError((error) => of(notificationSubscriptionError({ error })))
    )
  );

  notificationToSnackbar$ = createEffect(() =>
    this.actions.pipe(
      ofType(didReceiveNotification),
      map((action) =>
        ShowSnackBar(
          this.routeForNotification(action.payload)
            ? new SnackBarData(
                action.payload?.notification?.body,
                this.translate.instant('notifications.viewNow'),
                () => this.routeToNotification(action.payload)
              )
            : {
                message: action.payload?.notification?.body,
              }
        )
      )
    )
  );

  routeForNotification(notification: FirebaseNotificationPayload): [any[], NavigationExtras?] {
    if (!notification?.data) {
      return;
    }
    switch (notification.data?.modelName) {
      case 'Announcement':
        return [['notices', notification.data.modelId]];
      case 'Chat':
        return [['chats', notification.data.modelId]];
      case 'OrganisationFeedback':
        return [['dashboard'], { queryParams: { tab: 'feedback' } }];
      case 'Customer':
      case 'CheckIn':
        return [['communities', notification.data.modelId]];
      case 'Memo':
      case 'Comment':
        return [['messages', notification.data.modelId]];
    }
  }

  routeToNotification(notification: FirebaseNotificationPayload) {
    if (!notification) {
      return;
    }
    const routeParams = this.routeForNotification(notification);
    if (!routeParams) {
      return;
    }
    const orgId = notification.data?.organisationId;
    const [commands, navigationExtras] = routeParams;
    const orgRelativeCommands = ['/', 'organisations', orgId, ...commands];
    this.router.navigate(orgRelativeCommands, navigationExtras);
  }

  constructor(
    private actions: Actions<any>,
    private store: Store<any>,
    private pushService: PushService,
    private afMessaging: AngularFireMessaging,
    private translate: TranslateService,
    private router: Router
  ) {}
}
